import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';
import { StatusCode } from '@noths/polaris-dev-ts-types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import getConfig from 'next/config';

import type {
  ContactPartnerState,
  ContactPartnerSubmitPayload,
  EnquiriesAPIPostData,
  MarketingPreferencesSubmitPayload,
} from 'src/components/molecules/ContactPartnerModal/types';
import type { ProductState } from 'src/redux/product/types';
import type { NextConfig } from 'src/types/nextConfig';

interface MinimalState {
  product: ProductState;
}

const { publicRuntimeConfig } = getConfig() as NextConfig;

export const contactPartnerSubmit = createAsyncThunk<
  null,
  ContactPartnerSubmitPayload,
  { rejectValue: ContactPartnerState['error'] }
>(
  'contactPartnerModal/contactPartnerSubmit',
  async ({ marketingOptIn, message, userAttributes }, { dispatch, getState, rejectWithValue }) => {
    const { product } = getState() as MinimalState;
    const enquiriesUrl = `${publicRuntimeConfig.endpoints.enquiries}${product.code}/web_enquiries`;

    if (userAttributes === null) {
      return rejectWithValue({
        message: ['Please login to contact the seller.'],
      });
    }

    const { email: emailFromAccount, family_name, idToken, name } = userAttributes;

    const postData: EnquiriesAPIPostData = {
      name: name && family_name ? `${name} ${family_name}` : '',
      email: emailFromAccount || '',
      message,
    };

    try {
      const response = await fetchJSONWithTimeout(enquiriesUrl, {
        method: 'POST',
        headers: {
          ...(idToken && {
            'x-id-token': idToken,
          }),
        },
        body: postData as unknown as Record<string, unknown>,
        timeout: 2000,
      });

      if ([StatusCode.UnprocessableEntity, StatusCode.TooManyRequests].includes(response.status)) {
        const { errors } = await response.json();

        return rejectWithValue(errors);
      }

      if (marketingOptIn) {
        dispatch(submitMarketingPreferences({ email: postData.email }));
      }

      return null;
    } catch (error) {
      return rejectWithValue({
        message: ['An error occurred while contacting the seller'],
      });
    }
  },
);

export const submitMarketingPreferences = createAsyncThunk<null, MarketingPreferencesSubmitPayload>(
  'contactPartnerModal/submitMarketingPreferences',
  async ({ email }) => {
    const marketingPreferencesAnonymousUrl = `${publicRuntimeConfig.hosts.communicationPreferences}/anonymous`;

    return await fetchJSONWithTimeout(marketingPreferencesAnonymousUrl, {
      method: 'POST',
      body: {
        address: email,
        channel: 'email',
        status: 'ENABLED',
        source: 'ASK_SELLER',
      },
      timeout: 3000,
    }).then((response) => response.json());
  },
);
